import { useMemo } from 'react';
import { type Chord, buildChord, CHORD_VARIANTS } from '@theory/chords';
import type { Fingering } from '@shared-types/fingering';
import { useAppStore } from '@app/store/useAppStore';
import { parseChordId } from '@hooks/chordIdParser';
import { lookupChord, tuningCoveredByLibrary } from '@util/chordLibrary';

export interface ResolvedChord extends Chord {
  fingerings: Fingering[];
  variantIds: string[];
  /** True when this tuning has no chord coverage. */
  unavailable?: boolean;
}

export function useChord(chordId: string): ResolvedChord | null {
  const tuningId = useAppStore((s) => s.selectedTuningId);

  return useMemo(() => {
    if (!chordId) return null;

    const parsed = parseChordId(chordId);
    if (!parsed) return null;

    const { root, quality } = parsed;
    const chord = buildChord(root, quality);

    const variantQualities = CHORD_VARIANTS[quality] ?? [];
    const variantIds = variantQualities.map((q) => buildChord(root, q).id);

    if (!tuningCoveredByLibrary(tuningId)) {
      return { ...chord, fingerings: [], variantIds, unavailable: true };
    }

    const fingerings = lookupChord(tuningId, root, quality);
    return {
      ...chord,
      fingerings,
      variantIds,
      unavailable: fingerings.length === 0,
    };
  }, [chordId, tuningId]);
}
